import React, { useEffect } from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import BackBar from "../../../../common/nav/BackBar";
import SaveButton from "../../../../common/nav/SaveButton";
import ToggleInput from "../../../../common/forms/ToggleInput";
import NotificationModal from "../../../../common/settings-modal/NotificationModal";
import LoadingSpinner from "../../../../common/LoadingSpinner";
import { MailPrefsWithoutNecessary } from "../../../../common/classes/SonicUserMeta";

import SelenaA from "../../../../assets/img/service/selena-a.webp";

import { RequestState } from "../../../../utils/RequestState";

import styles from "../EditBase.module.scss";
import mailStyles from "./MailDeliverySetting.module.scss";

type Props = {
  mailPreferences: MailPrefsWithoutNecessary;
  isChanged: boolean;
  handlePrefs: (value: MailPrefsWithoutNecessary) => void;
  handleClose: () => void;
  handleSubmit: () => void;
  requestState: RequestState;
};

const MailDeliverySetting: React.FC<Props> = ({
  mailPreferences,
  isChanged,
  handlePrefs,
  handleClose,
  handleSubmit,
  requestState,
}) => {
  const genNotificationColor = () => {
    switch (requestState) {
      case RequestState.Pending:
      case RequestState.Failed:
        return "#fe493d";
      case RequestState.Doing:
        return "#f79b00";
      case RequestState.Success:
        return "#27c900";
      default:
        return "";
    }
  };

  const genNotificationMessage = () => {
    switch (requestState) {
      case RequestState.Pending:
        return "保存していない変更があります";
      case RequestState.Doing:
        return "通信中です";
      case RequestState.Success:
        return "保存が完了しました";
      case RequestState.Failed:
        return "保存に失敗しました";
      default:
        return "";
    }
  };

  const genNotificationIcon = (): IconProp | undefined => {
    switch (requestState) {
      case RequestState.Pending:
      case RequestState.Failed:
        return ["fal", "exclamation-circle"];
      case RequestState.Success:
        return ["fal", "check-circle"];
      default:
        return undefined;
    }
  };

  useEffect(() => {
    if (requestState === RequestState.Doing) {
      window.scrollTo(0, 0);
    }
  }, [requestState]);

  return (
    <div>
      {requestState !== RequestState.Doing ? (
        <>
          <BackBar
            title="メール配信設定"
            editable={true}
            handleClose={handleClose}
            saveHandler={handleSubmit}
            hideSaveButton={
              requestState !== RequestState.Pending ? true : false
            }
          />
          <div className={`${styles["p-edit__inner"]} ${styles["x-padding"]}`}>
            <div className={styles["p-edit__title"]}>
              メール配信設定
              <div className={styles["p-edit__title-desc"]}>
                SELeCTからのメールをカテゴリごとに受信/拒否の設定ができます。
                <br />
                オフに設定すると対象メールの受信を拒否することができます。
                <br />
                ※運営からの重要なお知らせや、オファー受付についてのメールは受信拒否することはできません。
                <div className={mailStyles["p-mail-setting"]}>
                  <p>ボタン説明</p>
                  <div className={mailStyles["p-mail-setting__item"]}>
                    受信しない
                    <div className={mailStyles["p-mail-setting__howto"]}>
                      <input id="toggle-demo" disabled={true} />
                      <label htmlFor="toggle-demo" />
                    </div>
                  </div>
                  <div className={mailStyles["p-mail-setting__item"]}>
                    受信する
                    <div
                      className={`${mailStyles["p-mail-setting__howto"]} ${mailStyles["active"]}`}
                    >
                      <input
                        id="toggle-active-demo"
                        checked={true}
                        disabled={true}
                      />
                      <label htmlFor="toggle-active-demo" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <form>
                <div className={styles["p-edit__form"]}>
                  <ToggleInput
                    label="プロフィール"
                    name="profile"
                    id="profile"
                    value={mailPreferences.portfolios}
                    description={`対象メール：\nプロフィール申請受け付け\nプロフィール公開完了`}
                    onChange={() => {
                      handlePrefs({
                        ...mailPreferences,
                        portfolios: !mailPreferences.portfolios,
                      });
                    }}
                  />
                  <ToggleInput
                    label="チーム"
                    name="team"
                    id="team"
                    value={mailPreferences.teams}
                    description={`対象メール：\nチーム申請受け付け\nチーム公開完了`}
                    onChange={() => {
                      handlePrefs({
                        ...mailPreferences,
                        teams: !mailPreferences.teams,
                      });
                    }}
                  />
                  <ToggleInput
                    label="オファー"
                    name="offer"
                    id="offer"
                    value={mailPreferences.offers}
                    description={`対象メール：\nオファー依頼送信完了`}
                    onChange={() => {
                      handlePrefs({
                        ...mailPreferences,
                        offers: !mailPreferences.offers,
                      });
                    }}
                  />
                  <ToggleInput
                    label="プロジェクト"
                    name="project"
                    id="project"
                    value={mailPreferences.projects}
                    description={`対象メール：\nプロジェクト申請受け付け\nプロジェクト公開完了\nプロジェクト応募完了`}
                    onChange={() => {
                      handlePrefs({
                        ...mailPreferences,
                        projects: !mailPreferences.projects,
                      });
                    }}
                  />
                  <ToggleInput
                    label="イベント"
                    name="event"
                    id="event"
                    value={mailPreferences.events}
                    description={`対象メール：\nイベント申請受け付け\nイベント公開完了`}
                    onChange={() => {
                      handlePrefs({
                        ...mailPreferences,
                        events: !mailPreferences.events,
                      });
                    }}
                  />
                  <ToggleInput
                    label="トピックス"
                    name="topics"
                    id="topics"
                    value={mailPreferences.topics}
                    description={`対象メール：\nトピックス申請受け付け\nトピックス公開完了`}
                    onChange={() => {
                      handlePrefs({
                        ...mailPreferences,
                        topics: !mailPreferences.topics,
                      });
                    }}
                  />
                  <ToggleInput
                    label="施設"
                    name="facility"
                    id="facility"
                    value={mailPreferences.facilities}
                    description={`対象メール：\n施設申請受け付け\n施設公開完了`}
                    onChange={() => {
                      handlePrefs({
                        ...mailPreferences,
                        facilities: !mailPreferences.facilities,
                      });
                    }}
                  />
                  <ToggleInput
                    label="チャット"
                    name="chat"
                    id="chat"
                    value={mailPreferences.chat}
                    description={`対象メール：\nチャットのメール受信`}
                    onChange={() => {
                      handlePrefs({
                        ...mailPreferences,
                        chat: !mailPreferences.chat,
                      });
                    }}
                  />
                </div>
              </form>
              {requestState === RequestState.Pending ? (
                <SaveButton title="保存" onClick={handleSubmit} />
              ) : null}
            </div>
          </div>
        </>
      ) : (
        <LoadingSpinner space={1} />
      )}
      {isChanged || requestState !== RequestState.Waiting ? (
        <NotificationModal
          color={genNotificationColor()}
          icon_class={genNotificationIcon()}
          message={genNotificationMessage()}
          img_src={requestState === RequestState.Success ? SelenaA : undefined}
        />
      ) : null}
    </div>
  );
};

export default MailDeliverySetting;
